/*
 * pdp-countdown.js
 * ---------------------------------------------------------------
 * "Order within 9h 11m for dispatch today" under the buy button.
 *
 * The element ships hidden and is only revealed once the script has
 * confirmed the cutoff has not already passed today. A dispatch promise
 * that cannot be met is worse than no promise at all, so after the
 * cutoff the line stays hidden rather than rolling over to tomorrow and
 * still claiming "today".
 *
 * The cutoff is a wall-clock time in the store's timezone. Liquid renders
 * the store's current UTC offset alongside it, so the countdown is the
 * same for a customer in Perth and one in London.
 *
 * Vanilla ES6, no dependencies.
 * ---------------------------------------------------------------
 */

(function () {
  'use strict';

  function offsetMinutes(raw) {
    // "+1000" / "-0430"
    var match = /^([+-])(\d{2})(\d{2})$/.exec((raw || '').trim());
    if (!match) return null;
    var sign = match[1] === '-' ? -1 : 1;
    return sign * (parseInt(match[2], 10) * 60 + parseInt(match[3], 10));
  }

  function storeNow(offMin) {
    var now = new Date();
    // Shift real time into the store's wall clock.
    return new Date(now.getTime() + (offMin + now.getTimezoneOffset()) * 60000);
  }

  function format(ms) {
    var totalMinutes = Math.floor(ms / 60000);
    var hours = Math.floor(totalMinutes / 60);
    var minutes = totalMinutes % 60;
    if (hours > 0) return hours + 'h ' + minutes + 'm';
    return minutes + 'm';
  }

  function setup(el) {
    /* Dawn re-renders the whole product-info block when a variant is
       chosen, so this element is replaced by a fresh, still-hidden copy
       of itself. Nothing re-ran setup on it, so picking the 2-pack made
       the countdown disappear until the page was reloaded. The flag
       stops a surviving element being bound twice. */
    if (el.dataset.countdownReady === 'true') return;
    el.dataset.countdownReady = 'true';

    var cutoff = /^(\d{1,2}):(\d{2})$/.exec((el.dataset.cutoff || '').trim());
    var offMin = offsetMinutes(el.dataset.utcOffset);
    var value = el.querySelector('[data-pdp-countdown-value]');
    if (!cutoff || offMin === null || !value) return;

    var cutoffHour = parseInt(cutoff[1], 10);
    var cutoffMinute = parseInt(cutoff[2], 10);
    /* Weekdays unless told otherwise. Without this, a Saturday morning read
       "for dispatch today" on a warehouse that does not dispatch on
       Saturdays -- the same kind of false promise the cutoff check exists to
       prevent. `storeNow` shifts the clock into store time, so getDay() is
       the store's day of the week, not the visitor's. Public holidays are
       not known here. */
    var everyDay = (el.dataset.days || '') === 'everyday';

    function tick() {
      var local = storeNow(offMin);

      var day = local.getDay();
      if (!everyDay && (day === 0 || day === 6)) {
        el.hidden = true;
        return;
      }
      var target = new Date(local);
      target.setHours(cutoffHour, cutoffMinute, 0, 0);

      var remaining = target.getTime() - local.getTime();

      // Past today's cutoff: say nothing rather than promise tomorrow.
      if (remaining <= 0) {
        el.hidden = true;
        return;
      }

      value.textContent = format(remaining);
      el.hidden = false;
    }

    tick();

    /* Every 30s for an hours-and-minutes readout, and stopped as soon as
       the element leaves the document -- a variant change can replace
       this element several times in a session, and each orphan would
       otherwise keep its own timer running for the life of the page. */
    var timer = setInterval(function () {
      if (!el.isConnected) {
        clearInterval(timer);
        return;
      }
      tick();
    }, 30000);
  }

  function init() {
    document.querySelectorAll('[data-pdp-countdown]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-bind whenever the buy box is re-rendered: a variant change swaps
     the markup without a page load, and the theme editor does the same
     on every edit. Watching for the element itself is more reliable than
     guessing which event the theme fires. */
  var host = document.querySelector('product-info, .product__info-container') || document.body;
  new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      for (var j = 0; j < records[i].addedNodes.length; j++) {
        var node = records[i].addedNodes[j];
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches('[data-pdp-countdown]')) {
          setup(node);
        } else if (node.querySelectorAll) {
          node.querySelectorAll('[data-pdp-countdown]').forEach(setup);
        }
      }
    }
  }).observe(host, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', init);
})();
